import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './PdfViewer.css';

const PdfViewer = ({ pdfUrl, title = 'PDF Document' }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (pdfUrl) {
      const timer = setTimeout(() => {
        if (isLoading) {
          console.log('Loading document is taking longer than usual...');
        }
      }, 15000);

      return () => clearTimeout(timer);
    }
  }, [pdfUrl, isLoading]);

  const handleLoadSuccess = () => {
    setIsLoading(false);
    setError(null);
  };

  const handleLoadError = () => {
    setIsLoading(false);
    setError('Unable to load document. Please try again later.');
  };

  const downloadPDF = async (url, fileName) => {
    try {
      setIsDownloading(true);
      
      const response = await fetch(url);
      if (!response.ok) throw new Error('Failed to download PDF');
      
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      window.URL.revokeObjectURL(downloadUrl);
      setIsDownloading(false);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      setIsDownloading(false);
    }
  };

  const sharePDFViaEmail = (url) => {
    const subject = 'Check out this PDF!';
    const body = `${title} pdf: ${url}`;
    const mailtoLink = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.open(mailtoLink, '_blank');
  };

  if (error) {
    return (
      <div className="pdf-viewer-container">
        <header className="pdf-viewer-header">
          <button className="back-button" onClick={() => navigate(-1)}>
            ← Back
          </button>
          <h1>{title}</h1>
          <button 
            className="share-button"
            onClick={() => sharePDFViaEmail(pdfUrl)}
          >
            Share
          </button>
        </header>
        <div className="error-container">
          <div className="error-content">
            <div className="error-icon">⚠️</div>
            <h2>Failed to load PDF</h2>
            <p>{error}</p>
            <button 
              className="retry-button"
              onClick={() => {
                setError(null);
                setIsLoading(true);
              }}
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pdf-viewer-container">
      <header className="pdf-viewer-header">
        <button className="back-button" onClick={() => navigate(-1)}>
          ← Back
        </button>
        <h1>{title}</h1>
        <button 
          className="share-button"
          onClick={() => sharePDFViaEmail(pdfUrl)}
        >
          Share
        </button>
      </header>

      <div className="pdf-content">
        {isLoading && (
          <div className="loading-overlay">
            <div className="loading-spinner"></div>
            <p>Loading PDF...</p>
          </div>
        )}
        
        {pdfUrl && (
          <iframe
            src={`${pdfUrl}#toolbar=0&navpanes=0&scrollbar=0`}
            title={title}
            width="100%"
            height="100%"
            onLoad={handleLoadSuccess}
            onError={handleLoadError}
            style={{ border: 'none' }}
          />
        )}
      </div>

      <button
        className={`download-fab ${isDownloading ? 'downloading' : ''}`}
        onClick={() => downloadPDF(pdfUrl, 'document.pdf')}
        disabled={isDownloading}
      >
        {isDownloading ? (
          <div className="download-spinner"></div>
        ) : (
          '⬇'
        )}
      </button>
    </div>
  );
};

export default PdfViewer;